/**
 * Page catalogue `<smootify-product data-id="filter">` — filtres client-side.
 *
 * Responsabilités :
 *   1. Observer les clones multi (`[data-lmdr-multi-clone]`) créés par
 *      `loadMany("filter")` au fur et à mesure de leur hydratation.
 *   2. Appliquer les filtres actifs sur chaque clone :
 *        - input[name="Search*"] → match sur product.title / product.vendor
 *        - input[filter-price="from"|"to"] → range min/max sur price
 *   3. Cacher les clones qui ne matchent pas (class `.lmdr-filter-hidden`).
 *   4. Hydrater `[filter="count"]` avec le nombre de clones visibles.
 *   5. Hydrater `[filter="empty-state"]` (visible si 0 clones match).
 *
 * Le styling du masquage est géré via critical-style.ts
 * (`.lmdr-filter-hidden { display: none !important }`).
 *
 * Ré-évalue tous les clones à chaque input user (debounce 150ms).
 */

import { writeToUrl, restoreFromUrl } from "./url-state.js";

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface FilterState {
  search: string;
  priceMin: number | null;
  priceMax: number | null;
  /** Liste des collection handles cochées (OR within). [] = pas de filtre. */
  collections: string[];
  /** Liste des tags cochés (OR within). [] = pas de filtre. */
  tags: string[];
  /** Tri Smootify : "" | "relevance" | "price_asc" | "price_desc"
   *  | "best_selling" | "created_asc" | "created_dsc"
   *  | "title_asc" | "title_dsc" */
  sort: SortKey;
}

type SortKey =
  | ""
  | "relevance"
  | "price_asc"
  | "price_desc"
  | "best_selling"
  | "created_asc"
  | "created_dsc"
  | "title_asc"
  | "title_dsc";

// ---------------------------------------------------------------------------
//  State helpers
// ---------------------------------------------------------------------------

function readState(): FilterState {
  if (typeof document === "undefined") {
    return { search: "", priceMin: null, priceMax: null, collections: [], tags: [], sort: "" };
  }
  const candidates = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'input[filter-search], input.search-field, input[name^="Search"]',
    ),
  );
  const searchValue = candidates.map((c) => c.value).find((v) => v?.trim().length > 0) ?? "";
  const fromInput = document.querySelector<HTMLInputElement>(
    '[filter-price="from"]',
  );
  const toInput = document.querySelector<HTMLInputElement>(
    '[filter-price="to"]',
  );

  // Facets : checkboxes [filter-collection="<handle>"] et [filter-tag="<tag>"]
  // cochées. Multi-value = OR within (au moins un match).
  const collections = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'input[filter-collection]:checked, input[filter-collection][checked="true"]',
    ),
  )
    .map((el) => el.getAttribute("filter-collection") ?? "")
    .filter((s) => s.length > 0);
  const tags = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'input[filter-tag]:checked, input[filter-tag][checked="true"]',
    ),
  )
    .map((el) => (el.getAttribute("filter-tag") ?? "").toLowerCase())
    .filter((s) => s.length > 0);

  // Tri : <select filter-sort>, <select name="sort">, ou [data-sort]
  const sortEl = document.querySelector<HTMLSelectElement | HTMLInputElement>(
    'select[filter-sort], select[name="sort"], [data-sort], input[filter-sort]:checked',
  );
  const sortRaw = (sortEl?.value ?? sortEl?.getAttribute("data-sort") ?? "").trim();
  const sort = normalizeSort(sortRaw);

  return {
    search: searchValue.trim().toLowerCase(),
    priceMin: parsePrice(fromInput?.value),
    priceMax: parsePrice(toInput?.value),
    collections,
    tags,
    sort,
  };
}

function normalizeSort(raw: string): SortKey {
  // Accept multiple aliases / casings vers les clés Smootify canoniques
  const v = raw.toLowerCase().replace(/[\s-]/g, "_");
  const map: Record<string, SortKey> = {
    "": "",
    "relevance": "relevance",
    "price": "price_asc",
    "price_asc": "price_asc",
    "price_desc": "price_desc",
    "newest": "created_dsc",
    "new": "created_dsc",
    "created": "created_dsc",
    "created_asc": "created_asc",
    "created_dsc": "created_dsc",
    "created_desc": "created_dsc",
    "best_selling": "best_selling",
    "bestselling": "best_selling",
    "alpha": "title_asc",
    "title": "title_asc",
    "title_asc": "title_asc",
    "title_dsc": "title_dsc",
    "title_desc": "title_dsc",
  };
  return map[v] ?? "";
}

function parsePrice(v: string | undefined): number | null {
  if (!v) return null;
  const n = Number.parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
//  Match
// ---------------------------------------------------------------------------

interface SmootifyProductLike extends HTMLElement {
  product?: {
    title?: string;
    vendor?: string;
    tags?: string[];
    variants?: Array<{
      price?: { amount?: string | number };
      availableForSale?: boolean;
    }>;
  } | null;
}

function matchesFilter(clone: SmootifyProductLike, state: FilterState): boolean {
  const p = clone.product;

  // Filter prix : utilise les data-attrs posés par loadMany depuis l'endpoint
  // worker (handle → [priceMin, priceMax]). Marche AUSSI sur les clones lazy
  // non-hydratés. Si pas d'attrs (cas legacy / endpoint vieux), fallback sur
  // c.product.variants.
  if (state.priceMin != null || state.priceMax != null) {
    const attrMin = Number.parseFloat(
      clone.getAttribute("data-lmdr-price-min") ?? "",
    );
    const attrMax = Number.parseFloat(
      clone.getAttribute("data-lmdr-price-max") ?? "",
    );
    let minP: number, maxP: number;
    if (Number.isFinite(attrMin) && Number.isFinite(attrMax)) {
      minP = attrMin;
      maxP = attrMax;
    } else if (p?.variants && p.variants.length > 0) {
      const prices = p.variants
        .map((v) => Number.parseFloat(String(v.price?.amount ?? "")))
        .filter((n) => Number.isFinite(n));
      if (prices.length === 0) return true;
      minP = Math.min(...prices);
      maxP = Math.max(...prices);
    } else {
      // Pas de prix dispo → laisse passer (edge case).
      return true;
    }
    if (state.priceMin != null && maxP < state.priceMin) return false;
    if (state.priceMax != null && minP > state.priceMax) return false;
  }

  // Search : utilise le blob `data-lmdr-search` posé par loadMany depuis
  // l'endpoint worker (titre + tags concaténés en lowercase, sans vendor).
  // Marche INSTANTANÉMENT sur 1500+ produits SANS hydratation lazy.
  // Fallback sur c.product si l'endpoint n'a pas livré le blob.
  if (state.search) {
    const blob = clone.getAttribute("data-lmdr-search");
    if (blob) {
      if (!blob.includes(state.search)) return false;
    } else if (p) {
      const haystack = [p.title ?? "", ...(p.tags ?? [])]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(state.search)) return false;
    }
    // Si ni blob ni p → laisse passer (edge case).
  }

  // Facet collections : OR within (au moins une collection cochée doit
  // matcher les collections du clone). Pas de filter actif → laisse passer.
  if (state.collections.length > 0) {
    const cloneCols = (clone.getAttribute("data-lmdr-collections") ?? "")
      .split(",")
      .filter((s) => s.length > 0);
    const hasMatch = state.collections.some((c) => cloneCols.includes(c));
    if (!hasMatch) return false;
  }

  // Facet tags : OR within (au moins un tag coché doit matcher).
  if (state.tags.length > 0) {
    const cloneTags = (clone.getAttribute("data-lmdr-tags") ?? "")
      .split(",")
      .filter((s) => s.length > 0);
    const hasMatch = state.tags.some((t) => cloneTags.includes(t));
    if (!hasMatch) return false;
  }

  return true;
}

/**
 * Compare 2 clones selon le critère de tri Smootify.
 * Retourne <0, 0, >0 (Array.sort signature).
 */
function compareClones(
  a: SmootifyProductLike,
  b: SmootifyProductLike,
  sort: SortKey,
): number {
  if (!sort || sort === "relevance") return 0;

  const getPrice = (el: SmootifyProductLike): number => {
    const min = Number.parseFloat(el.getAttribute("data-lmdr-price-min") ?? "");
    return Number.isFinite(min) ? min : 0;
  };
  const getCreatedAt = (el: SmootifyProductLike): number => {
    const ts = Number.parseInt(el.getAttribute("data-lmdr-created-at") ?? "0", 10);
    return Number.isFinite(ts) ? ts : 0;
  };
  const getTitle = (el: SmootifyProductLike): string => {
    // Prefer hydrated product.title, fallback sur data-handle (qui contient titre slugifié)
    const t = el.product?.title ?? el.getAttribute("data-handle") ?? "";
    return t.toLowerCase();
  };

  switch (sort) {
    case "price_asc":
      return getPrice(a) - getPrice(b);
    case "price_desc":
      return getPrice(b) - getPrice(a);
    case "created_asc":
      return getCreatedAt(a) - getCreatedAt(b);
    case "created_dsc":
      return getCreatedAt(b) - getCreatedAt(a);
    case "title_asc":
      return getTitle(a).localeCompare(getTitle(b));
    case "title_dsc":
      return getTitle(b).localeCompare(getTitle(a));
    case "best_selling":
      // Pas de donnée best-selling côté headless-handles. Fallback sur price_desc.
      return getPrice(b) - getPrice(a);
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
//  Apply
// ---------------------------------------------------------------------------

/**
 * Re-évalue tous les clones `filter` et applique la class `.lmdr-filter-hidden`
 * sur ceux qui ne matchent pas. Met à jour `[filter="count"]`, `[filter="empty-state"]`
 * et le chip `[filter="active"]` + `[filter="active-label"]`.
 */
export function applyFilters(): void {
  if (typeof document === "undefined") return;

  const clones = document.querySelectorAll<SmootifyProductLike>(
    'smootify-product[data-lmdr-multi-clone="true"][data-lmdr-multi-kind="filter"]',
  );
  if (clones.length === 0) return;

  const state = readState();

  // Lit les bornes du slider pour détecter "full range = pas de filtre actif".
  // Si state.priceMin === range.min ET state.priceMax === range.max, l'user
  // n'a pas vraiment filtré → on n'affiche pas le chip "filtre actif".
  const rangeFrom = document.querySelector<HTMLInputElement>(
    'filter-price input[type="range"], .range-containers input[type="range"]',
  );
  const allRanges = document.querySelectorAll<HTMLInputElement>(
    'filter-price input[type="range"], .range-containers input[type="range"]',
  );
  const rangeMin = rangeFrom ? Number.parseFloat(rangeFrom.min) : null;
  const rangeMax =
    allRanges.length >= 2 ? Number.parseFloat(allRanges[1]!.max) : null;
  const priceFilterActive =
    (state.priceMin != null && rangeMin != null && state.priceMin > rangeMin) ||
    (state.priceMax != null && rangeMax != null && state.priceMax < rangeMax);

  const hasFilter =
    state.search.length > 0 ||
    priceFilterActive ||
    state.collections.length > 0 ||
    state.tags.length > 0;

  let visible = 0;
  const visibleList: SmootifyProductLike[] = [];
  clones.forEach((el) => {
    const ok = matchesFilter(el, state);
    el.classList.toggle("lmdr-filter-hidden", !ok);
    if (ok) {
      visible++;
      visibleList.push(el);
    }
  });

  // Tri : reorder visibleList puis re-insert dans le DOM dans le nouvel ordre.
  // Skip si sort === "" (default order = ordre DOM original).
  if (state.sort && state.sort !== "relevance") {
    visibleList.sort((a, b) => compareClones(a, b, state.sort));
    // Re-order DOM : insertBefore chaque clone selon le nouvel ordre, en
    // utilisant le 1er parent commun comme container.
    const parent = visibleList[0]?.parentElement;
    if (parent) {
      // Lit la position du dernier clone visible avant tri pour ne pas
      // bouger les clones cachés (lmdr-filter-hidden) entre les visibles.
      visibleList.forEach((el) => {
        parent.appendChild(el);
      });
    }
  }

  // Pagination : applique le slice de la page courante sur les clones visibles.
  // Cache via `.lmdr-pagination-hidden` (sibling de `.lmdr-filter-hidden`).
  applyPaginationSlice(visibleList);

  // Notifie smootify-product (loadMany) qu un round de filtre/pagination
  // vient d etre applique. Sert a re-tenter la lazy hydration sur les cards
  // qui viennent de redevenir visibles (page nav, filtre change).
  document.dispatchEvent(
    new CustomEvent("smootify:filter_count", {
      detail: { visible, total: clones.length, page: currentPage },
    }),
  );

  // [filter="count"] : si aucun filtre → total (plus lisible UX), sinon visible.
  const displayed = hasFilter ? visible : clones.length;
  document.querySelectorAll<HTMLElement>('[filter="count"]').forEach((el) => {
    el.textContent = String(displayed);
  });

  // [filter="empty-state"] : visible SI zéro match.
  document
    .querySelectorAll<HTMLElement>('[filter="empty-state"]')
    .forEach((el) => {
      el.classList.toggle("lmdr-filter-hidden", visible > 0);
    });

  // Chip "filtre actif" : visible uniquement si vraie interaction de filtrage
  // (search non-vide OU price range plus serré que les bornes du slider).
  const activeBtns = document.querySelectorAll<HTMLElement>('[filter="active"]');
  const activeLabels = document.querySelectorAll<HTMLElement>(
    '[filter="active-label"]',
  );
  let activeLabel: string | null = null;
  if (state.search.length > 0) {
    activeLabel = capitalize(state.search);
  } else if (state.collections.length > 0) {
    activeLabel = state.collections.map(capitalize).join(", ");
  } else if (state.tags.length > 0) {
    activeLabel = state.tags.map(capitalize).join(", ");
  } else if (priceFilterActive) {
    activeLabel = priceRangeLabel(state.priceMin, state.priceMax);
  }
  activeBtns.forEach((el) => {
    el.classList.toggle("lmdr-filter-hidden", !activeLabel);
  });
  activeLabels.forEach((el) => {
    el.textContent = activeLabel ?? "";
  });

  // 1er refresh réussi avec des clones → wrapper smootify-search-discovery
  // peut être révélé (fade-in du sidebar/pagination/empty-state, pour
  // remplacer les placeholders Webflow par notre rendu propre).
  const ssd = document.querySelector("smootify-search-discovery");
  if (ssd && !ssd.classList.contains("lmdr-ready")) {
    ssd.classList.add("lmdr-ready");
  }

  // Sync URL avec l'état courant (no-op si sync-query-params absent).
  // Lit les bornes du slider pour ne pas écrire pmin/pmax si l'user n'a pas
  // resserré le range au-delà des bornes globales.
  const ranges = document.querySelectorAll<HTMLInputElement>(
    'filter-price input[type="range"], .range-containers input[type="range"]',
  );
  const urlRangeMin = ranges[0] ? Number.parseFloat(ranges[0].min) : null;
  const urlRangeMax = ranges[1] ? Number.parseFloat(ranges[1].max) : null;
  writeToUrl({
    search: state.search,
    priceMin: state.priceMin,
    priceMax: state.priceMax,
    sort: state.sort,
    collections: state.collections,
    tags: state.tags,
    page: currentPage,
    rangeMin: urlRangeMin,
    rangeMax: urlRangeMax,
  });
}

function capitalize(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function priceRangeLabel(
  min: number | null,
  max: number | null,
): string | null {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}€ – ${max}€`;
  if (min != null) return `≥ ${min}€`;
  return `≤ ${max}€`;
}

// ---------------------------------------------------------------------------
//  Bind inputs
// ---------------------------------------------------------------------------

let debounceTimer: number | undefined;
function scheduleApply(): void {
  if (typeof window === "undefined") return;
  if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    applyFilters();
  }, 120);
}

/**
 * Attache les listeners sur les inputs search/price/reset. Idempotent.
 * Re-tourner safe sur chaque hydratation — on marque les nodes avec
 * `data-lmdr-filter-bound`.
 */
export function attachFilterInputs(root: ParentNode = document): void {
  // On ne bind PAS `input[name="query"]` (header search bar qui redirige vers
  // /search) — il ne sert qu'à la Predictive Search et doit rester libre.
  const searches = root.querySelectorAll<HTMLInputElement>(
    'input[filter-search], input.search-field, input[name^="Search"]',
  );
  searches.forEach((input) => {
    if (input.hasAttribute("data-lmdr-filter-bound")) return;
    input.setAttribute("data-lmdr-filter-bound", "true");
    input.addEventListener("input", () => {
      currentPage = 1; // reset page sur changement de filtre
      scheduleApply();
    });
    // Bloque la touche Enter : sinon le form parent Webflow soumet et
    // affiche "Thank you! Your submission has been received!" au-dessus
    // des résultats. Le filtrage est déjà live via `input` event.
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        ev.stopPropagation();
      }
    });
    // Bloque aussi le submit au niveau du form parent : si l'user clique
    // sur un bouton submit ailleurs ou si Webflow déclenche le submit via
    // un autre chemin, on intercepte.
    const form = input.closest("form");
    if (form && !form.hasAttribute("data-lmdr-filter-form-bound")) {
      form.setAttribute("data-lmdr-filter-form-bound", "true");
      form.addEventListener(
        "submit",
        (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        },
        true,
      );
    }
  });

  const prices = root.querySelectorAll<HTMLInputElement>(
    '[filter-price="from"], [filter-price="to"]',
  );
  prices.forEach((input) => {
    if (input.hasAttribute("data-lmdr-filter-bound")) return;
    input.setAttribute("data-lmdr-filter-bound", "true");
    const onChange = (): void => {
      currentPage = 1;
      scheduleApply();
    };
    input.addEventListener("input", onChange);
    input.addEventListener("change", onChange);
  });

  // Facets : checkboxes/radios collection + tag
  const facets = root.querySelectorAll<HTMLInputElement>(
    'input[filter-collection], input[filter-tag]',
  );
  facets.forEach((input) => {
    if (input.hasAttribute("data-lmdr-filter-bound")) return;
    input.setAttribute("data-lmdr-filter-bound", "true");
    input.addEventListener("change", () => {
      currentPage = 1;
      scheduleApply();
    });
  });

  // Tri : select / radio
  const sortInputs = root.querySelectorAll<HTMLElement>(
    'select[filter-sort], select[name="sort"], input[filter-sort], [data-sort]',
  );
  sortInputs.forEach((input) => {
    if (input.hasAttribute("data-lmdr-filter-bound")) return;
    input.setAttribute("data-lmdr-filter-bound", "true");
    const handler = (): void => {
      currentPage = 1;
      scheduleApply();
    };
    input.addEventListener("change", handler);
    // Si c'est un button/div avec data-sort (custom sort UI), bind aussi click
    if (input.tagName !== "SELECT" && input.tagName !== "INPUT") {
      input.addEventListener("click", handler);
    }
  });

  // Bouton "Réinitialiser" — on cherche plusieurs patterns :
  //   - [data-action="reset-filter"] / [data-action="filter-reset"]
  //   - button.reset-button / .reset-filter (Webflow class conventions)
  //   - button[type="reset"] dans le form filter
  //   - [filter="active"] (le chip X pour retirer le filtre actif)
  const resetButtons = root.querySelectorAll<HTMLElement>(
    '[data-action="reset-filter"], [data-action="filter-reset"], button.reset-button, button.reset-filter, .filter-reset, [filter="active"], button[type="reset"]',
  );
  resetButtons.forEach((btn) => {
    if (btn.hasAttribute("data-lmdr-filter-bound")) return;
    btn.setAttribute("data-lmdr-filter-bound", "true");
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      // Clear inputs texte/prix
      root
        .querySelectorAll<HTMLInputElement>(
          'input[name^="Search"], input.search-field, input[name="query"], [filter-price="from"], [filter-price="to"]',
        )
        .forEach((inp) => {
          inp.value = "";
          inp.dispatchEvent(new Event("input", { bubbles: true }));
        });
      // Décoche les facets (collections + tags)
      root
        .querySelectorAll<HTMLInputElement>(
          'input[filter-collection]:checked, input[filter-tag]:checked',
        )
        .forEach((inp) => {
          inp.checked = false;
        });
      // Reset le tri à default
      root
        .querySelectorAll<HTMLSelectElement>(
          'select[filter-sort], select[name="sort"]',
        )
        .forEach((sel) => {
          sel.selectedIndex = 0;
        });
      currentPage = 1;
      scheduleApply();
    });
  });
}

/**
 * À appeler une fois au boot + à chaque nouveau clone hydraté. Re-attache les
 * listeners (idempotent), recalcule les bounds du price slider depuis les
 * clones hydratés, et ré-applique les filtres.
 */
export function refreshFilters(root: ParentNode = document): void {
  attachFilterInputs(root);
  bindPriceSlider(root);
  updatePriceSliderBounds(root);
  attachPaginationButtons(root);

  // Au 1er passage : restore l'état depuis l'URL (sync-query-params).
  // Pose aussi currentPage si présent dans l'URL.
  if (!urlRestored) {
    urlRestored = true;
    restoreFromUrl(root === document ? document : document);
    if (typeof window !== "undefined") {
      const urlPage = Number.parseInt(
        new URLSearchParams(window.location.search).get("page") ?? "1",
        10,
      );
      if (Number.isFinite(urlPage) && urlPage > 1) {
        currentPage = urlPage;
      }
    }
  }

  applyFilters();
}

let urlRestored = false;

// ---------------------------------------------------------------------------
//  Pagination (slice client-side)
// ---------------------------------------------------------------------------

/**
 * Pagination : on garde tous les clones hydratés en DOM (pour permettre
 * le filtre client-side instantané) mais on n'AFFICHE que `pageLimit`
 * cards à la fois, slice de la page courante.
 *
 * Lit `<smootify-search-discovery limit="40">` pour la taille de page.
 * Si pas de wrapper, default 40.
 */
let currentPage = 1;
let pageLimit = 40;

function readPaginationConfig(): void {
  const ssd = document.querySelector("smootify-search-discovery");
  if (!ssd) return;
  const lim = Number.parseInt(ssd.getAttribute("limit") ?? "40", 10);
  if (Number.isFinite(lim) && lim > 0) pageLimit = lim;
}

/**
 * Applique le slice de la page courante sur les clones VISIBLES (post-filtre).
 * Cache les clones hors de la slice via `.lmdr-pagination-hidden`.
 * Met à jour les boutons prev/next (disabled state).
 */
function applyPaginationSlice(visibleClones: HTMLElement[]): void {
  readPaginationConfig();

  const totalPages = Math.max(1, Math.ceil(visibleClones.length / pageLimit));
  if (currentPage > totalPages) currentPage = totalPages;
  if (currentPage < 1) currentPage = 1;

  const startIdx = (currentPage - 1) * pageLimit;
  const endIdx = startIdx + pageLimit;

  visibleClones.forEach((el, i) => {
    el.classList.toggle("lmdr-pagination-hidden", i < startIdx || i >= endIdx);
  });

  // Update buttons disabled state
  const prevBtn = document.querySelector<HTMLButtonElement>(
    'button[name="previous"], .pagination button[name="previous"]',
  );
  const nextBtn = document.querySelector<HTMLButtonElement>(
    'button[name="next"], .pagination button[name="next"]',
  );
  if (prevBtn) {
    prevBtn.disabled = currentPage <= 1;
    prevBtn.classList.toggle("lmdr-pagination-disabled", currentPage <= 1);
  }
  if (nextBtn) {
    nextBtn.disabled = currentPage >= totalPages;
    nextBtn.classList.toggle("lmdr-pagination-disabled", currentPage >= totalPages);
  }

  // Hydrate compteurs page-courante / page-totale (optionnel, si Webflow
  // pose ces attributs)
  document
    .querySelectorAll<HTMLElement>('[filter="page-current"], [pagination="current"]')
    .forEach((el) => {
      el.textContent = String(currentPage);
    });
  document
    .querySelectorAll<HTMLElement>('[filter="page-total"], [pagination="total"]')
    .forEach((el) => {
      el.textContent = String(totalPages);
    });
}

/**
 * Bind les boutons de pagination prev/next. Idempotent.
 *
 * Convention Smootify : `<button name="previous">` et `<button name="next">`
 * dans un container `.pagination`.
 */
function attachPaginationButtons(root: ParentNode = document): void {
  const prevBtn = root.querySelector<HTMLButtonElement>(
    'button[name="previous"], .pagination button[name="previous"]',
  );
  const nextBtn = root.querySelector<HTMLButtonElement>(
    'button[name="next"], .pagination button[name="next"]',
  );

  if (prevBtn && !prevBtn.hasAttribute("data-lmdr-pagi-bound")) {
    prevBtn.setAttribute("data-lmdr-pagi-bound", "true");
    prevBtn.addEventListener("click", (ev) => {
      ev.preventDefault();
      if (currentPage > 1) {
        currentPage--;
        applyFilters();
        scrollToGridTop();
      }
    });
  }
  if (nextBtn && !nextBtn.hasAttribute("data-lmdr-pagi-bound")) {
    nextBtn.setAttribute("data-lmdr-pagi-bound", "true");
    nextBtn.addEventListener("click", (ev) => {
      ev.preventDefault();
      currentPage++;
      applyFilters();
      scrollToGridTop();
    });
  }
}

/**
 * Scroll tout en haut de la page quand on change de page de pagination.
 *
 * On scrolle au TOP du document (y=0) plutôt qu'au top du grid : sur LMDR la
 * grid est ~200px du top mais l'utilisateur veut typiquement revenir au début
 * de la page (header marketing + filtres en haut visible).
 *
 * Implémentation : double-fallback (scrollTo standard + scrollTop direct sur
 * documentElement et body) car certains contextes/browsers ignorent l'un ou
 * l'autre. On utilise behavior:'auto' (instant) car le smooth peut être
 * désactivé par le browser ou par un CSS parent.
 */
function scrollToGridTop(): void {
  if (typeof window === "undefined") return;
  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    window.scrollTo(0, 0);
  }
  // Fallbacks (Safari/iframe quirks)
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
}

// ---------------------------------------------------------------------------
//  Price slider (input type="range" ×2 ↔ inputs number [filter-price])
// ---------------------------------------------------------------------------

/**
 * Trouve les 2 `input[type="range"]` dans l'élément `<filter-price>` / container
 * et les binde pour qu'ils synchronisent avec les inputs `[filter-price="from"/"to"]`.
 * Idempotent.
 */
function bindPriceSlider(root: ParentNode): void {
  const rangeInputs = root.querySelectorAll<HTMLInputElement>(
    'filter-price input[type="range"], .range-containers input[type="range"]',
  );
  if (rangeInputs.length < 2) return;

  const fromNumber = root.querySelector<HTMLInputElement>('[filter-price="from"]');
  const toNumber = root.querySelector<HTMLInputElement>('[filter-price="to"]');

  const [rangeFrom, rangeTo] = Array.from(rangeInputs);

  const sync = (opts: { apply?: boolean } = {}): void => {
    // Identifie lequel est "from" (valeur la plus basse) vs "to" — Webflow
    // ne met pas forcément dans l'ordre DOM ce qu'on attend.
    const v1 = Number.parseFloat(rangeFrom!.value) || 0;
    const v2 = Number.parseFloat(rangeTo!.value) || 0;
    const min = Math.min(v1, v2);
    const max = Math.max(v1, v2);
    if (fromNumber) fromNumber.value = String(min);
    if (toNumber) toNumber.value = String(max);
    // Met à jour les labels visuels s'ils existent.
    const host = rangeFrom!.closest("filter-price") ?? root;
    host
      .querySelectorAll<HTMLElement>(
        '[filter-price-label="from"], .range-label-from, .price-from-label',
      )
      .forEach((el) => setFormatted(el, min));
    host
      .querySelectorAll<HTMLElement>(
        '[filter-price-label="to"], .range-label-to, .price-to-label',
      )
      .forEach((el) => setFormatted(el, max));
    host
      .querySelectorAll<HTMLElement>('[filter-price-label="range"]')
      .forEach((el) => (el.textContent = `${formatEuro(min)} – ${formatEuro(max)}`));

    // Injecte / met à jour notre propre label auto (sous le slider) si aucun
    // label officiel n'est défini dans le template Webflow.
    ensureAutoLabel(rangeFrom!, min, max);

    // Met à jour la barre rouge `[filter-price="progress"]` (Webflow ne le
    // fait pas tout seul quand value est changée par JS).
    const rangeMin = Number.parseFloat(rangeFrom!.min) || 0;
    const rangeMax = Number.parseFloat(rangeTo!.max) || 0;
    updateProgressBar(rangeMin, rangeMax, min, max);

    if (opts.apply !== false) {
      scheduleApply();
    }
  };

  [rangeFrom!, rangeTo!].forEach((inp) => {
    if (inp.hasAttribute("data-lmdr-filter-bound")) return;
    inp.setAttribute("data-lmdr-filter-bound", "true");
    inp.addEventListener("input", () => sync());
    inp.addEventListener("change", () => sync());
  });

  // 1re synchronisation au bind : met l'UI prix à jour sans relancer applyFilters().
  sync({ apply: false });
}

function setFormatted(el: HTMLElement, value: number): void {
  el.textContent = formatEuro(value);
}

function formatEuro(value: number): string {
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)}€`;
  }
}

/**
 * Auto-label (Webflow ne pose pas de label par défaut sous le slider).
 * On crée un `<div data-lmdr-price-auto-label>` sous le container range
 * qui affiche "X€ – Y€" en live.
 */
function ensureAutoLabel(
  rangeInput: HTMLInputElement,
  min: number,
  max: number,
): void {
  const container =
    rangeInput.closest<HTMLElement>(".range-containers") ??
    (rangeInput.parentElement as HTMLElement | null);
  if (!container) return;
  const host = container.parentElement ?? container;
  let label = host.querySelector<HTMLElement>(
    ':scope > [data-lmdr-price-auto-label]',
  );
  // Si un label officiel [filter-price-label] existe, on skip notre auto-label.
  const hasOfficial = host.querySelector(
    '[filter-price-label]',
  );
  if (hasOfficial) {
    label?.remove();
    return;
  }
  if (!label) {
    label = document.createElement("div");
    label.setAttribute("data-lmdr-price-auto-label", "");
    label.style.cssText =
      "display:flex;justify-content:space-between;margin-top:6px;font-variant-numeric:tabular-nums;";
    container.insertAdjacentElement("afterend", label);
  }
  // Recalcule la font-family depuis le label voisin Webflow (typiquement
  // `field-label-2` Min, `field-label-3` Max → Cochinltstd). Évite que les
  // montants apparaissent en Satoshi (font body par défaut) alors que le
  // reste du filtre est en Cochin. CSS `font:inherit` ne suffit pas car
  // Webflow pose une règle plus spécifique sur les divs enfants du body.
  const referenceLabel = host.querySelector<HTMLElement>(
    '[filter-price="from-label"], [filter-price="to-label"], .field-label-2, .field-label-3, .field-label',
  );
  const inheritFont = referenceLabel
    ? getComputedStyle(referenceLabel).fontFamily
    : "Cochinltstd, sans-serif";
  label.innerHTML = `<span style="font-family:${inheritFont}">${formatEuro(min)}</span><span style="font-family:${inheritFont}">${formatEuro(max)}</span>`;
}

/**
 * Met à jour visuellement la barre `[filter-price="progress"]` Webflow.
 *
 * Webflow l'update normalement via son custom element `<filter-price>`, mais
 * quand on change `value` programmatiquement (notre code de bind), Webflow
 * ne réagit pas toujours. On fait le calcul nous-mêmes :
 * `left = (min - rangeMin) / span * 100%`, `right = (rangeMax - max) / span * 100%`.
 */
function updateProgressBar(
  rangeMin: number,
  rangeMax: number,
  curMin: number,
  curMax: number,
): void {
  const progress = document.querySelector<HTMLElement>('[filter-price="progress"]');
  if (!progress) return;
  const span = rangeMax - rangeMin;
  if (span <= 0) return;
  const leftPct = ((curMin - rangeMin) / span) * 100;
  const rightPct = ((rangeMax - curMax) / span) * 100;
  progress.style.left = `${Math.max(0, leftPct).toFixed(2)}%`;
  progress.style.right = `${Math.max(0, rightPct).toFixed(2)}%`;
  progress.style.width = "auto";
  progress.style.maxWidth = "100%";
}

/**
 * Track si l'utilisateur a déjà interagi avec le slider (vrai event utilisateur,
 * pas event programmatique). Tant que false, `updatePriceSliderBounds` peut
 * réajuster les values pour rester aligné sur globalMin/globalMax (qui s'affine
 * au fur et à mesure que les clones se peuplent).
 *
 * Une fois l'user a touché (event.isTrusted === true), on ne touche plus à
 * `value` — sinon on écraserait son intention.
 */
let userInteractedWithSlider = false;

function trackSliderInteraction(rangeInputs: NodeListOf<HTMLInputElement> | HTMLInputElement[]): void {
  Array.from(rangeInputs).forEach((r) => {
    if (r.hasAttribute("data-lmdr-slider-tracked")) return;
    r.setAttribute("data-lmdr-slider-tracked", "true");
    const onTouch = (e: Event): void => {
      if (e.isTrusted) userInteractedWithSlider = true;
    };
    r.addEventListener("input", onTouch);
    r.addEventListener("change", onTouch);
  });
}

/**
 * Calcule les bounds min/max des prix depuis les clones filter hydratés
 * et applique sur les 2 range inputs.
 *
 * Recalcule à chaque `refreshFilters()` — pendant que les clones se peuplent
 * progressivement, les bounds s'affinent et les values initiales suivent.
 *
 * Bug corrigé : avant, on settait `value` au 1er hydraté (race condition →
 * value=18/18 si 1er produit à 18€). Maintenant on tracke l'interaction user
 * via `event.isTrusted` et on n'arrête de réajuster qu'après la 1re interaction.
 */
function updatePriceSliderBounds(root: ParentNode): void {
  const rangeInputs = root.querySelectorAll<HTMLInputElement>(
    'filter-price input[type="range"], .range-containers input[type="range"]',
  );
  if (rangeInputs.length < 2) return;

  trackSliderInteraction(rangeInputs);

  // Source #1 (préférée) : data-attrs `data-lmdr-price-min/max` sur les clones,
  // posés au moment de la création depuis l'endpoint worker enrichi. Marche
  // immédiatement, même avant hydratation.
  // Source #2 (fallback) : prix calculés depuis les products hydratés.
  const clones = root.querySelectorAll<SmootifyProductLike>(
    'smootify-product[data-lmdr-multi-kind="filter"]',
  );
  let globalMin = Infinity;
  let globalMax = -Infinity;
  let counted = 0;
  clones.forEach((c) => {
    const attrMin = Number.parseFloat(c.getAttribute("data-lmdr-price-min") ?? "");
    const attrMax = Number.parseFloat(c.getAttribute("data-lmdr-price-max") ?? "");
    if (Number.isFinite(attrMin) && Number.isFinite(attrMax)) {
      counted++;
      if (attrMin < globalMin) globalMin = attrMin;
      if (attrMax > globalMax) globalMax = attrMax;
      return;
    }
    // Fallback : product hydraté
    const prices = (c.product?.variants ?? [])
      .map((v) => Number.parseFloat(String(v.price?.amount ?? "")))
      .filter((n) => Number.isFinite(n));
    if (prices.length === 0) return;
    counted++;
    const pMin = Math.min(...prices);
    const pMax = Math.max(...prices);
    if (pMin < globalMin) globalMin = pMin;
    if (pMax > globalMax) globalMax = pMax;
  });

  // Si rien d'hydraté encore : on ne touche surtout pas aux values (sinon on
  // colle un 0-500 fallback qui mask presque tout). On laisse les ranges en
  // l'état (Webflow a posé ses defaults), mais on ne pose pas non plus min/max
  // sur les inputs (ils s'updateront au prochain refresh).
  if (counted === 0) return;

  globalMin = Math.floor(globalMin);
  globalMax = Math.ceil(globalMax);

  const [rangeFrom, rangeTo] = Array.from(rangeInputs);

  // Pose toujours min/max/step (peu importe l'interaction user — c'est la
  // bornage du widget).
  rangeFrom!.min = String(globalMin);
  rangeFrom!.max = String(globalMax);
  rangeFrom!.step = "1";
  rangeTo!.min = String(globalMin);
  rangeTo!.max = String(globalMax);
  rangeTo!.step = "1";

  // Values initiales : on ne pose les bornes complètes que SI l'user n'a pas
  // encore interagi avec le slider. Tant qu'il n'a pas touché, on s'aligne sur
  // globalMin/globalMax (qui s'affine au fur et à mesure des hydratations).
  if (userInteractedWithSlider) return;

  const currentFrom = Number.parseFloat(rangeFrom!.value);
  const currentTo = Number.parseFloat(rangeTo!.value);
  const needUpdate =
    currentFrom !== globalMin || currentTo !== globalMax;
  if (!needUpdate) return;

  rangeFrom!.value = String(globalMin);
  rangeTo!.value = String(globalMax);
  // Dispatch SANS bubbling pour ne pas être confondu avec un event user
  // (mais isTrusted=false de toute façon → trackSliderInteraction l'ignore).
  rangeFrom!.dispatchEvent(new Event("input", { bubbles: true }));
}
